(() => {
  if (customElements.get('cz-purchase-bar')) return;

  class CarzillaPurchaseBar extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.variantSelect = this.querySelector('[data-cz-purchase-variant]');
      this.quantity = this.querySelector('[data-cz-purchase-quantity]');
      this.addButton = this.querySelector('[data-cz-purchase-add]');
      this.price = this.querySelector('[data-cz-purchase-price]');
      this.addAriaLabel = this.addAriaLabel || this.addButton.getAttribute('aria-label');
      this.minus = this.querySelector('[data-cz-purchase-minus]');
      this.plus = this.querySelector('[data-cz-purchase-plus]');
      this.mobile = matchMedia('(max-width: 768px)');
      this.mobile.addEventListener('change', () => this.bindProduct(), { signal });
      document.addEventListener('page:loaded', () => this.bindProduct(), { signal });
      document.addEventListener('shopify:section:load', () => this.bindProduct(), { signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (this.section && event.target.contains(this.section)) this.unbindProduct();
      }, { signal });
      this.variantSelect.addEventListener('change', () => this.selectVariant(), { signal });
      this.quantity.addEventListener('change', () => this.setQuantity(this.quantity.value), { signal });
      this.minus.addEventListener('click', () => this.adjustQuantity(-1), { signal });
      this.plus.addEventListener('click', () => this.adjustQuantity(1), { signal });
      this.addButton.addEventListener('click', () => this.submitOriginal(), { signal });
      this.bindProduct();
    }

    disconnectedCallback() {
      this.unbindProduct();
      this.controller?.abort();
      this.controller = null;
    }

    unbindProduct() {
      this.productController?.abort();
      this.intersection?.disconnect();
      this.resize?.disconnect();
      this.buttonObserver?.disconnect();
      this.priceObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.productController = null;
      this.section = null;
      this.form = null;
      this.nativePrice = null;
      this.hidden = true;
    }

    bindProduct() {
      this.unbindProduct();
      if (!this.mobile.matches || !window.theme?.Variants) return;
      const section = document.querySelector(`.cz-product-layout[data-product-id="${this.dataset.productId}"]`);
      const form = section?.querySelector('form.product-single__form');
      const nativeButton = form?.querySelector('button[data-add-to-cart]');
      const master = form?.querySelector('[data-product-select]');
      const nativeQuantity = form && Array.from(form.elements).find((el) => el.name === 'quantity');
      const json = section?.querySelector('[data-variant-json]');
      if (!form || !nativeButton || !master || !nativeQuantity || !json) return;
      let variants;
      try { variants = JSON.parse(json.value || json.textContent); } catch { return; }
      if (!Array.isArray(variants) || !variants.length) return;
      this.section = section;
      this.form = form;
      this.nativeButton = nativeButton;
      this.nativePrice = section.querySelector('[data-product-price]');
      this.master = master;
      this.nativeQuantity = nativeQuantity;
      this.variants = variants;
      this.currentVariant = variants.find((variant) => String(variant.id) === master.value) || null;
      this.productController = new AbortController();
      const signal = this.productController.signal;
      const controls = Array.from(section.querySelectorAll('[data-variant-input]'));
      this.optionControls = controls;
      const hasOptions = controls.length > 0;
      this.variantSelect.hidden = !hasOptions;
      this.variantSelect.replaceChildren();
      if (hasOptions) {
        variants.forEach((variant) => {
          const option = new Option(variant.title, String(variant.id));
          option.disabled = !variant.available;
          this.variantSelect.add(option);
        });
      }
      section.addEventListener('variantChange', (event) => {
        this.currentVariant = event.detail.variant;
        queueMicrotask(() => { if (this.form) this.sync(); });
      }, { signal });
      const syncLater = () => queueMicrotask(() => { if (this.form) this.sync(); });
      section.addEventListener('change', syncLater, { signal });
      section.addEventListener('input', syncLater, { signal });
      section.addEventListener('click', syncLater, { signal });
      form.addEventListener('ajaxProduct:error', () => {
        const error = form.querySelector('.errors');
        (error || nativeButton).scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, { signal });
      form.addEventListener('invalid', (event) => {
        event.target.scrollIntoView({ block: 'center' });
      }, { capture: true, signal });
      this.buttonObserver = new MutationObserver(() => this.sync());
      this.buttonObserver.observe(nativeButton, { attributes: true, childList: true, characterData: true, subtree: true });
      if (this.nativePrice) {
        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.nativePrice, { childList: true, characterData: true, subtree: true });
      }
      this.nav = document.querySelector('.cz-mobile-bottom-nav');
      this.resize = new ResizeObserver(() => this.updatePosition());
      if (this.nav) this.resize.observe(this.nav);
      this.resize.observe(nativeButton);
      this.intersection = new IntersectionObserver(() => this.updatePosition(), { threshold: [0, 1] });
      this.intersection.observe(nativeButton);
      window.addEventListener('resize', () => this.updatePosition(), { passive: true, signal });
      window.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updatePosition();
        });
      }, { passive: true, signal });
      this.sync();
      this.updatePosition();
    }

    sync() {
      if (!this.form) return;
      const selected = this.currentVariant;
      if (selected) this.variantSelect.value = String(selected.id);
      else this.variantSelect.selectedIndex = -1;
      if (document.activeElement !== this.quantity) this.quantity.value = this.nativeQuantity.value;
      const rule = this.quantityRule();
      this.quantity.min = String(rule.min);
      this.quantity.step = String(rule.step);
      if (Number.isFinite(rule.max)) this.quantity.max = String(rule.max);
      else this.quantity.removeAttribute('max');
      this.minus.disabled = Number(this.nativeQuantity.value) <= rule.min;
      this.plus.disabled = Number(this.nativeQuantity.value) >= rule.max;
      const busy = this.nativeButton.classList.contains('btn--loading') || this.nativeButton.getAttribute('aria-busy') === 'true';
      const terms = this.form.querySelector('.js-terms-checkbox');
      const needsTerms = Boolean(selected?.available && terms && !terms.checked);
      this.setAttribute('aria-busy', String(busy));
      this.toggleAttribute('data-terms-required', needsTerms);
      this.addButton.disabled = busy || !selected?.available || (needsTerms ? terms.disabled : this.nativeButton.disabled);
      const nativeLabel = this.nativeButton.querySelector('[data-add-to-cart-text]')?.textContent.trim() || this.nativeButton.textContent.trim();
      const label = needsTerms ? this.addButton.dataset.termsLabel : selected?.available && !this.nativeButton.disabled ? this.addButton.dataset.addLabel : nativeLabel;
      if (label && this.addButton.textContent !== label) this.addButton.textContent = label;
      this.addButton.setAttribute('aria-label', label === this.addButton.dataset.addLabel ? this.addAriaLabel : label);
      this.syncPrice();
    }

    syncPrice() {
      if (!this.form || !this.price) return;
      const source = this.nativePrice?.querySelector('.cz_p1') || this.nativePrice;
      let fullPrice = '';
      if (source && this.currentVariant) {
        const copy = source.cloneNode(true);
        copy.querySelectorAll('.visually-hidden, .sr-only, [hidden]').forEach((node) => node.remove());
        const decimal = /amount_with_(?:comma|space)_separator/.test(window.theme?.settings?.moneyFormat || '') ? ',' : '.';
        copy.querySelectorAll('sup').forEach((node) => {
          if (/^\d{2}$/.test(node.textContent.trim())) node.prepend(decimal);
        });
        fullPrice = copy.textContent.replace(/\s+/g, ' ').trim();
      }
      const compactPrice = fullPrice.replace(/\s+(CAD|USD)$/, '');
      this.price.hidden = !compactPrice;
      if (this.price.textContent !== compactPrice) this.price.textContent = compactPrice;
      this.price.setAttribute('aria-label', `${this.price.dataset.priceLabel}: ${fullPrice}`);
      this.price.title = fullPrice;
    }

    quantityRule() {
      const rule = this.currentVariant?.quantity_rule || {};
      const min = Math.max(1, Number(this.nativeQuantity.min) || 1, Number(rule.min) || 1);
      const step = Math.max(1, Number(this.nativeQuantity.step) || 1, Number(rule.increment) || 1);
      const max = Math.min(Number(this.nativeQuantity.max) || Infinity, Number(rule.max) || Infinity);
      return { min, step, max };
    }

    setQuantity(value) {
      if (!this.form) return;
      const { min, step, max } = this.quantityRule();
      const numeric = Number(value);
      let amount = Number.isFinite(numeric) ? Math.max(min, Math.floor(numeric)) : min;
      amount = min + Math.floor((amount - min) / step) * step;
      if (amount > max) amount = min + Math.floor((max - min) / step) * step;
      this.nativeQuantity.value = String(amount);
      this.nativeQuantity.dispatchEvent(new Event('input', { bubbles: true }));
      this.nativeQuantity.dispatchEvent(new Event('change', { bubbles: true }));
      this.quantity.value = this.nativeQuantity.value;
      this.sync();
    }

    adjustQuantity(direction) {
      if (!this.form) return;
      this.setQuantity(Number(this.nativeQuantity.value) + direction * this.quantityRule().step);
    }

    selectVariant() {
      if (!this.form) return;
      const variant = this.variants.find((item) => String(item.id) === this.variantSelect.value);
      if (!variant?.available) { this.sync(); return; }
      let changed;
      this.optionControls.forEach((control) => {
        const value = variant[control.dataset.index];
        if (control.type === 'radio' || control.type === 'checkbox') {
          control.checked = control.value === value;
          if (control.checked) changed = control;
        } else {
          control.value = value;
          changed = control;
        }
      });
      if (changed) changed.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
    }

    submitOriginal() {
      if (!this.form?.isConnected) { this.bindProduct(); return; }
      this.sync();
      if (this.addButton.disabled) return;
      const terms = this.form.querySelector('.js-terms-checkbox');
      if (terms && !terms.checked) {
        terms.checked = true;
        terms.dispatchEvent(new Event('input', { bubbles: true }));
        terms.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
        return;
      }
      this.setQuantity(this.quantity.value);
      this.nativeButton.click();
      this.sync();
    }

    updatePosition() {
      if (!this.form) return;
      const rect = this.nativeButton.getBoundingClientRect();
      const navHeight = this.nav?.getBoundingClientRect().height || 0;
      if (navHeight) this.style.setProperty('--cz-purchase-nav-height', `${navHeight}px`);
      const wasHidden = this.hidden;
      this.hidden = !this.mobile.matches || rect.height === 0 || rect.bottom > 0;
      if (wasHidden && !this.hidden) this.sync();
    }
  }

  customElements.define('cz-purchase-bar', CarzillaPurchaseBar);
})();


